import { ConfirmAction } from "@atlas/composite";
import { Form, FormInputField, FormSelectField, useToast } from "@atlas/core";
import { EngineEvent, useEventPublish, useTranslation } from "@atlas/sdk";
import { zodResolver } from "@hookform/resolvers/zod";
import type { IUpdateUserRequest, IUserInfo } from "@models";
import { getUserInfoKey, useUpdateUser } from "@query";
import { APP_BASE_PATH } from "@shared";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const HUB_LOCALE_DEFS = [
  { value: "en-US", labelKey: "hub.locale_switch.en_US" },
  { value: "fr-FR", labelKey: "hub.locale_switch.fr_FR" },
  { value: "es-ES", labelKey: "hub.locale_switch.es_ES" },
  { value: "de-DE", labelKey: "hub.locale_switch.de_DE" },
  { value: "ar", labelKey: "hub.locale_switch.ar" },
] as const;

function isActiveLocale(
  active: string | undefined,
  optionCode: string,
): boolean {
  if (!active) return false;
  if (active === optionCode) return true;
  const opt = optionCode.toLowerCase();
  const cur = active.toLowerCase();
  return cur === opt || cur.startsWith(`${opt}-`) || opt.startsWith(`${cur}-`);
}

const createEditProfileSchema = (t: (key: string) => string) =>
  z.object({
    firstName: z.string().min(1, t("hub.profile.first_name_required")),
    lastName: z.string().min(1, t("hub.profile.last_name_required")),
    language: z.string().min(1, t("hub.profile.language_required")),
  });

type EditProfileFormValues = z.infer<
  ReturnType<typeof createEditProfileSchema>
>;

interface EditProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: IUserInfo;
}

export function EditProfileDialog({
  open,
  onOpenChange,
  user,
}: EditProfileDialogProps) {
  const { t, language } = useTranslation();
  const publish = useEventPublish();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { mutate: updateUser, isPending } = useUpdateUser(
    user.companyUuid,
    APP_BASE_PATH,
  );

  const editProfileSchema = createEditProfileSchema(t);

  const languageOptions = useMemo(
    () =>
      HUB_LOCALE_DEFS.map((d) => ({
        value: d.value,
        label: t(d.labelKey),
      })),
    [t],
  );

  const defaultLanguageValue = useMemo(() => {
    const found = HUB_LOCALE_DEFS.find((d) =>
      isActiveLocale(language, d.value),
    );
    return found?.value ?? HUB_LOCALE_DEFS[0].value;
  }, [language]);

  const computedValues = useMemo<EditProfileFormValues>(
    () => ({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      language: defaultLanguageValue,
    }),
    [user, defaultLanguageValue],
  );

  const form = useForm<EditProfileFormValues>({
    resolver: zodResolver(editProfileSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      language: "en-US",
    },
    values: open ? computedValues : undefined,
  });

  const onSubmit = (data: EditProfileFormValues) => {
    if (!user.id) return;

    const request: IUpdateUserRequest = {
      id: user.id,
      firstName: data.firstName,
      lastName: data.lastName,
      language: data.language,
      timezone: user.timezone || "UTC",
      email: user.email || "",
      userType: user.userType,
      applicationTenants:
        user.applicationTenants?.flatMap((app) =>
          (app.userTenants ?? []).map((ut) => ({
            id: ut.id,
            compAppMapId: ut.compAppMapId,
            applicationVersionId: app.applicationVersionId,
            tenantInstanceId: ut.tenantInstanceId,
          })),
        ) ?? [],
    };

    updateUser(
      { userId: user.id, request },
      {
        onSuccess: () => {
          publish.emit(EngineEvent.LanguageChanged, {
            language: data.language,
          });
          void queryClient.invalidateQueries({
            queryKey: getUserInfoKey(APP_BASE_PATH),
          });
          toast.add({
            title: t("hub.profile.update_success", {
              defaultValue: "Profile updated successfully",
            }),
            data: { type: "success" },
          });
          onOpenChange(false);
        },
        onError: () => {
          toast.add({
            title: t("hub.profile.update_error", {
              defaultValue: "Failed to update profile",
            }),
            data: { type: "error" },
          });
        },
      },
    );
  };

  return (
    <ConfirmAction
      open={open}
      onOpenChange={onOpenChange}
      title={t("hub.profile.edit_profile", { defaultValue: "Edit Profile" })}
      description={
        <Form {...form}>
          <form
            id="edit-profile-form"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 mt-4 mx-auto">
            <FormInputField<EditProfileFormValues>
              control={form.control}
              name="firstName"
              label={t("hub.profile.first_name", {
                defaultValue: "First Name",
              })}
              placeholder={t("hub.profile.first_name_placeholder", {
                defaultValue: "Enter your first name",
              })}
              description={t("hub.profile.first_name_description", {
                defaultValue: "Your given name.",
              })}
            />
            <FormInputField<EditProfileFormValues>
              control={form.control}
              name="lastName"
              label={t("hub.profile.last_name", { defaultValue: "Last Name" })}
              placeholder={t("hub.profile.last_name_placeholder", {
                defaultValue: "Enter your last name",
              })}
              description={t("hub.profile.last_name_description", {
                defaultValue: "Your family name.",
              })}
            />
            <FormSelectField<EditProfileFormValues>
              control={form.control}
              name="language"
              label={t("hub.profile.interface_language", {
                defaultValue: "Language",
              })}
              placeholder={t("hub.locale_switch.menu_label", {
                defaultValue: "Select language",
              })}
              description={t("hub.profile.language_description", {
                defaultValue: "The language used for the user interface.",
              })}
              items={languageOptions}
            />
          </form>
        </Form>
      }
      cancelText={t("hub.administer.cancel", { defaultValue: "Cancel" })}
      confirmText={t("hub.administer.save", { defaultValue: "Save" })}
      disabled={isPending}
      onConfirm={() => {
        const formElement = document.getElementById(
          "edit-profile-form",
        ) as HTMLFormElement;
        if (formElement) {
          formElement.requestSubmit();
        }
      }}
    />
  );
}
